import { useMemo, useState } from 'react'
import { ExternalLink, Inbox, Loader2, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  PLANNABLE_TYPES,
  type CalendarView,
  type PendingTask,
  type PlannableCode,
} from '@/api/resourceCalendarApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useCalendarMutations, usePendingTasks } from '@/hooks/useResourceCalendar'
import { UNASSIGNED, dayHeader, formatHours } from '@/lib/myTasks'
import { cn } from '@/lib/utils'
import { resourceProfilesApi } from '@/api/resourceCenterApi'
import { useSupportActivityTypes, useUnavailabilityTypes } from '@/hooks/useResourceCenter'
import { useProfileWrite } from '@/components/resource-center/profile/shared'
import { TypeBadge } from './ActivityCard'

/** Time away is picked from the Resource Center lists, so SA rides along with the plannable types. */
type PlanCode = PlannableCode | 'SA'
const PLAN_TYPES: PlanCode[] = [...PLANNABLE_TYPES, 'SA']

export interface PlanPreset {
  date?: string
  /** A user id, or null for Unassigned; undefined keeps the task's own owner. */
  ownerId?: string | null
}

/**
 * Plan a task that is already waiting — never invent one. Pick the type, pick
 * the pending task, give the day, the owner and a reason:
 *
 * - equipment and cleaning work moves the real task (its planned day and owner),
 * - reviews, CAPAs, kits, pick lists and process steps get a calendar entry
 *   linked to the record, so Open lands on it,
 * - unavailable (UA) and support activity (SA) are picked from the Resource
 *   Center lists and saved on the chosen person's profile, as there.
 *
 * The server checks qualification and availability either way; its refusals
 * show here, its warnings go back to the page.
 */
export function PlanTaskDialog({
  preset,
  initialTask = null,
  today,
  legend,
  people,
  onClose,
  onWarnings,
}: {
  preset: PlanPreset
  /** Picked already — "Plan" from the task queue. */
  initialTask?: PendingTask | null
  today: string
  legend: CalendarView['legend']
  people: { userId: string; name: string }[]
  onClose: () => void
  onWarnings: (warnings: string[]) => void
}) {
  const { create, move, refresh } = useCalendarMutations()
  const [type, setType] = useState<PlanCode>((initialTask?.type as PlannableCode | undefined) ?? 'EQ')
  const isTimeAway = type === 'UA' || type === 'SA'
  const pending = usePendingTasks(isTimeAway ? null : (type as PlannableCode))
  const [picked, setPicked] = useState<PendingTask | null>(initialTask)
  const [search, setSearch] = useState('')
  const [date, setDate] = useState(preset.date ?? today)
  // Undefined until chosen: then the task keeps whoever already has it.
  const [owner, setOwner] = useState<string | undefined>(
    preset.ownerId === undefined ? undefined : (preset.ownerId ?? UNASSIGNED),
  )
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  // UA / SA: a type from the center list, and its hours (blank = whole day for UA).
  const uaTypes = useUnavailabilityTypes()
  const saTypes = useSupportActivityTypes()
  const [centerId, setCenterId] = useState('')
  const [hours, setHours] = useState('')
  const write = useProfileWrite()

  const nameOf = (code: string) => legend.find((l) => l.code === code)?.name ?? code
  const busy = create.isPending || move.isPending || write.pending

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const rows = pending.data ?? []
    return needle
      ? rows.filter((t) => `${t.description} ${t.sourceRef ?? ''} ${t.ownerName ?? ''}`.toLowerCase().includes(needle))
      : rows
  }, [pending.data, search])

  const centerList = type === 'UA' ? uaTypes : saTypes
  const centerShown = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const rows = (centerList.data ?? []).filter((t) => t.active)
    return needle ? rows.filter((t) => `${t.code} ${t.name}`.toLowerCase().includes(needle)) : rows
  }, [centerList.data, search])
  const uaPicked = uaTypes.data?.find((t) => t.id === centerId)
  const saPicked = saTypes.data?.find((t) => t.id === centerId)

  const ownerId = (fallback: string | null): string | null =>
    owner === undefined ? fallback : owner === UNASSIGNED ? null : owner

  const person = people.find((p) => p.userId === owner)
  const hoursOk = type === 'SA' ? Number(hours) > 0 : hours === '' || Number(hours) > 0
  const ready = !!date && (isTimeAway ? !!person && !!centerId && hoursOk : !!reason.trim() && !!picked)

  const done = (warnings: string[]) => {
    onWarnings(warnings)
    onClose()
  }
  const fail = (e: Error) => setFailure(e.message)

  const submit = () => {
    setFailure(null)
    if (isTimeAway) {
      if (!person) return
      // Saved on the person's Resource Center profile; the calendar reads it from there.
      write.run(
        () =>
          type === 'UA'
            ? resourceProfilesApi.addUnavailability(person.userId, {
                unavailabilityTypeId: centerId,
                fromDate: date,
                toDate: date,
                hours: hours === '' ? null : Number(hours),
                note: reason.trim() || undefined,
              })
            : resourceProfilesApi.addSupportActivity(person.userId, {
                supportActivityTypeId: centerId,
                hoursPerDay: Number(hours),
                fromDate: date,
                toDate: date,
              }),
        () => {
          void refresh()
          onClose()
        },
      )
      return
    }
    if (!picked) return
    if (picked.kind === 'maintenance') {
      move.mutate(
        {
          key: picked.key as string,
          body: {
            date,
            ownerId: ownerId(picked.ownerId),
            version: picked.version as string,
            reason: reason.trim(),
          },
        },
        { onSuccess: (r) => done(r.warnings), onError: fail },
      )
      return
    }
    create.mutate(
      {
        type: picked.type as Exclude<PlannableCode, 'EQ' | 'CL' | 'UA'>,
        linkKind: picked.kind,
        linkId: picked.id,
        ownerId: ownerId(picked.ownerId),
        date,
        reason: reason.trim(),
      },
      { onSuccess: (r) => done(r.warnings), onError: fail },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex max-h-[90vh] flex-col gap-4 sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Plan a task</DialogTitle>
          <DialogDescription>Pick work that is already waiting and put it on someone’s day.</DialogDescription>
        </DialogHeader>

        {/* Type */}
        <div role='radiogroup' aria-label='Type of work' className='flex flex-wrap gap-1.5'>
          {PLAN_TYPES.map((t) => (
            <button
              key={t}
              type='button'
              role='radio'
              aria-checked={type === t}
              onClick={() => {
                setType(t)
                setPicked(null)
                setSearch('')
                setCenterId('')
                setHours('')
              }}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors hover:bg-muted',
                type === t && 'border-primary/40 bg-primary/10 font-medium text-primary',
              )}
            >
              <TypeBadge code={t} className='h-4 min-w-[1.5rem] text-[9px]' />
              {nameOf(t)}
            </button>
          ))}
        </div>

        {/* What */}
        {isTimeAway ? (
          <div className='flex min-h-0 flex-1 flex-col rounded-lg border'>
            <div className='flex items-center gap-2 border-b px-3 py-2'>
              <Search className='h-4 w-4 text-muted-foreground' />
              <input
                aria-label={`Search ${nameOf(type).toLowerCase()} types`}
                className='flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground'
                placeholder={`Search the center's ${nameOf(type).toLowerCase()} list`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {centerList.data && <span className='text-xs text-muted-foreground'>{centerShown.length} on the list</span>}
            </div>
            <ul role='listbox' aria-label={`${nameOf(type)} types`} className='max-h-72 min-h-[8rem] flex-1 overflow-y-auto p-1'>
              {centerList.isLoading ? (
                <li className='flex items-center gap-2 p-4 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' /> Loading the center list…
                </li>
              ) : centerList.isError ? (
                <li className='p-4 text-sm text-destructive'>{centerList.error.message}</li>
              ) : centerShown.length === 0 ? (
                <li className='flex flex-col items-center gap-1 p-6 text-center text-sm text-muted-foreground'>
                  <Inbox className='h-5 w-5' />
                  {search ? 'Nothing matches.' : 'None on the center list yet — add them in Resource Center.'}
                </li>
              ) : (
                centerShown.map((t) => {
                  const selected = centerId === t.id
                  return (
                    <li key={t.id} role='option' aria-selected={selected}>
                      <button
                        type='button'
                        onClick={() => {
                          setCenterId(t.id)
                          setHours('defaultHoursPerDay' in t ? String(t.defaultHoursPerDay) : '')
                        }}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted/70',
                          selected && 'bg-primary/10 ring-1 ring-primary/30',
                        )}
                      >
                        <TypeBadge code={type} />
                        <span className='min-w-0 flex-1'>
                          <span className='block truncate font-medium text-foreground'>{t.name}</span>
                          <span className='block truncate text-xs text-muted-foreground'>{t.code}</span>
                        </span>
                        <span className='shrink-0 text-right text-xs tabular-nums text-muted-foreground'>
                          {'defaultHoursPerDay' in t
                            ? `${formatHours(t.defaultHoursPerDay)} h / day`
                            : t.allowPartial
                              ? 'Full day or hours'
                              : 'Full day'}
                        </span>
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        ) : (
          <div className='flex min-h-0 flex-1 flex-col rounded-lg border'>
            <div className='flex items-center gap-2 border-b px-3 py-2'>
              <Search className='h-4 w-4 text-muted-foreground' />
              <input
                aria-label='Search pending tasks'
                className='flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground'
                placeholder={`Search pending ${nameOf(type).toLowerCase()} work`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {pending.data && <span className='text-xs text-muted-foreground'>{shown.length} waiting</span>}
            </div>
            <ul role='listbox' aria-label='Pending tasks' className='max-h-72 min-h-[8rem] flex-1 overflow-y-auto p-1'>
              {pending.isLoading ? (
                <li className='flex items-center gap-2 p-4 text-sm text-muted-foreground'>
                  <Loader2 className='h-4 w-4 animate-spin' /> Finding pending work…
                </li>
              ) : pending.isError ? (
                <li className='p-4 text-sm text-destructive'>{pending.error.message}</li>
              ) : shown.length === 0 ? (
                <li className='flex flex-col items-center gap-1 p-6 text-center text-sm text-muted-foreground'>
                  <Inbox className='h-5 w-5' />
                  Nothing {search ? 'matches' : 'is waiting'} for {nameOf(type)}.
                </li>
              ) : (
                shown.map((t) => {
                  const selected = picked?.id === t.id
                  return (
                    <li key={t.id} role='option' aria-selected={selected}>
                      <button
                        type='button'
                        onClick={() => setPicked(t)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted/70',
                          selected && 'bg-primary/10 ring-1 ring-primary/30',
                        )}
                      >
                        <TypeBadge code={t.type} />
                        <span className='min-w-0 flex-1'>
                          <span className='block truncate font-medium text-foreground'>{t.description}</span>
                          <span className='block truncate text-xs text-muted-foreground'>
                            {[t.sourceRef, t.ownerName ?? 'No owner', t.plannedDate && `on ${dayHeader(t.plannedDate)}`]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        <span className='shrink-0 text-right text-xs tabular-nums text-muted-foreground'>
                          <span className='block'>{t.dueDate ? `Due ${dayHeader(t.dueDate)}` : 'No due date'}</span>
                          <span className='block'>{formatHours(t.hours)} h</span>
                        </span>
                        {t.href && (
                          <Link
                            to={t.href}
                            onClick={(e) => e.stopPropagation()}
                            title='Open this task'
                            aria-label={`Open ${t.description}`}
                            className='shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground'
                          >
                            <ExternalLink className='h-3.5 w-3.5' />
                          </Link>
                        )}
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        )}

        {/* When, who, why */}
        <div className='grid grid-cols-2 gap-3 text-sm'>
          <div className='space-y-1'>
            <Label htmlFor='plan-date'>Day</Label>
            <Input id='plan-date' type='date' value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className='space-y-1'>
            <Label>Owner</Label>
            <Select value={owner ?? '__keep__'} onValueChange={(v) => setOwner(v === '__keep__' ? undefined : v)}>
              <SelectTrigger aria-label='Owner'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {!isTimeAway && <SelectItem value='__keep__'>Keep current owner</SelectItem>}
                {!isTimeAway && <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>}
                {people.map((p) => (
                  <SelectItem key={p.userId} value={p.userId}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isTimeAway && (
            <div className='space-y-1'>
              <Label htmlFor='plan-hours'>{type === 'SA' ? 'Hours' : 'Hours (blank = full day)'}</Label>
              <Input
                id='plan-hours'
                type='number'
                min={0.25}
                max={24}
                step={0.25}
                value={hours}
                disabled={type === 'UA' && !uaPicked?.allowPartial}
                placeholder={type === 'SA' ? String(saPicked?.defaultHoursPerDay ?? '') : 'Full day'}
                onChange={(e) => setHours(e.target.value)}
              />
            </div>
          )}
          {type !== 'SA' && (
            <div className='col-span-2 space-y-1'>
              <Label htmlFor='plan-reason'>{isTimeAway ? 'Note (optional)' : 'Reason (required)'}</Label>
              <Textarea
                id='plan-reason'
                rows={2}
                maxLength={500}
                value={reason}
                placeholder='Kept in the audit trail.'
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}
          {(failure ?? write.error) && <p className='col-span-2 text-destructive'>{failure ?? write.error}</p>}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!ready || busy}>
            {busy ? 'Planning…' : isTimeAway ? `Add ${nameOf(type).toLowerCase()}` : 'Plan task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
